import { Link, createFileRoute, notFound } from "@tanstack/react-router";
import { ArrowRight, CheckCircle2, Clock, Lock, ShieldCheck } from "lucide-react";
// Only the light modules statically: the corpus is dynamically imported in the loader.
import { SITE_URL } from "@/lib/site-url";
import { SUPPORTED_LANGS, isBlogLang } from "@/lib/blog-langs";
import type { BlogLang, BlogPost } from "@/lib/blog-types";

const UI: Record<
  BlogLang,
  {
    quickSummary: string;
    cost: string;
    time: string;
    privacy: string;
    refund: string;
    keywordsHeading: string;
    keywordsSub: string;
    stepsHeading: (app: string) => string;
    ctaTitle: (app: string) => string;
    ctaSub: string;
    ctaLabel: string;
    tipsHeading: string;
    faqHeading: string;
    back: string;
    home: string;
    blog: string;
  }
> = {
  en: {
    quickSummary: "Quick summary",
    cost: "Cost: less than $0.99 per SMS.",
    time: "Total time: 5 minutes.",
    privacy: "Privacy: your real number is never exposed.",
    refund: "Automatic refund if the code doesn't arrive.",
    keywordsHeading: "Keywords this guide answers",
    keywordsSub: "This tutorial matches searches like:",
    stepsHeading: (app) => `Steps to create ${app} without a real number`,
    ctaTitle: (app) => `Create your anonymous ${app} now`,
    ctaSub: "Rent a virtual number from $0.35 and receive the SMS in seconds.",
    ctaLabel: "Go to SMS Activations",
    tipsHeading: "Tips to keep your account anonymous",
    faqHeading: "Frequently asked questions",
    back: "← Back to the blog",
    home: "Dashboard",
    blog: "Blog",
  },
  es: {
    quickSummary: "Resumen rápido",
    cost: "Coste: menos de 0,99 $ por SMS.",
    time: "Tiempo total: 5 minutos.",
    privacy: "Privacidad: no expones tu número real.",
    refund: "Reembolso automático si el código no llega.",
    keywordsHeading: "Palabras clave que resuelve esta guía",
    keywordsSub: "Este tutorial responde a búsquedas como:",
    stepsHeading: (app) => `Pasos para crear ${app} sin número real`,
    ctaTitle: (app) => `Crea tu ${app} anónimo ahora`,
    ctaSub: "Alquila un número virtual desde 0,35 $ y recibe el SMS en segundos.",
    ctaLabel: "Ir a SMS Activations",
    tipsHeading: "Consejos para mantener tu cuenta anónima",
    faqHeading: "Preguntas frecuentes",
    back: "← Volver al blog",
    home: "Dashboard",
    blog: "Blog",
  },
  de: {
    quickSummary: "Resumen rápido",
    cost: "Coste: menos de 0,99 $ por SMS.",
    time: "Tiempo total: 5 minutos.",
    privacy: "Privacidad: no expones tu número real.",
    refund: "Reembolso automático si el código no llega.",
    keywordsHeading: "Palabras clave que resuelve esta guía",
    keywordsSub: "Este tutorial responde a búsquedas como:",
    stepsHeading: (app) => `Pasos para crear ${app} sin número real`,
    ctaTitle: (app) => `Crea tu ${app} anónimo ahora`,
    ctaSub: "Alquila un número virtual desde 0,35 $ y recibe el SMS en segundos.",
    ctaLabel: "Ir a SMS Activations",
    tipsHeading: "Consejos para mantener tu cuenta anónima",
    faqHeading: "Preguntas frecuentes",
    back: "← Volver al blog",
    home: "Dashboard",
    blog: "Blog",
  },
  fr: {
    quickSummary: "Resumen rápido",
    cost: "Coste: menos de 0,99 $ por SMS.",
    time: "Tiempo total: 5 minutos.",
    privacy: "Privacidad: no expones tu número real.",
    refund: "Reembolso automático si el código no llega.",
    keywordsHeading: "Palabras clave que resuelve esta guía",
    keywordsSub: "Este tutorial responde a búsquedas como:",
    stepsHeading: (app) => `Pasos para crear ${app} sin número real`,
    ctaTitle: (app) => `Crea tu ${app} anónimo ahora`,
    ctaSub: "Alquila un número virtual desde 0,35 $ y recibe el SMS en segundos.",
    ctaLabel: "Ir a SMS Activations",
    tipsHeading: "Consejos para mantener tu cuenta anónima",
    faqHeading: "Preguntas frecuentes",
    back: "← Volver al blog",
    home: "Tableau de bord",
    blog: "Blog",
  },
  it: {
    quickSummary: "Resumen rápido",
    cost: "Coste: menos de 0,99 $ por SMS.",
    time: "Tiempo total: 5 minutos.",
    privacy: "Privacidad: no expones tu número real.",
    refund: "Reembolso automático si el código no llega.",
    keywordsHeading: "Palabras clave que resuelve esta guía",
    keywordsSub: "Este tutorial responde a búsquedas como:",
    stepsHeading: (app) => `Pasos para crear ${app} sin número real`,
    ctaTitle: (app) => `Crea tu ${app} anónimo ahora`,
    ctaSub: "Alquila un número virtual desde 0,35 $ y recibe el SMS en segundos.",
    ctaLabel: "Ir a SMS Activations",
    tipsHeading: "Consejos para mantener tu cuenta anónima",
    faqHeading: "Preguntas frecuentes",
    back: "← Volver al blog",
    home: "Dashboard",
    blog: "Blog",
  },
  pt: {
    quickSummary: "Resumen rápido",
    cost: "Coste: menos de 0,99 $ por SMS.",
    time: "Tiempo total: 5 minutos.",
    privacy: "Privacidad: no expones tu número real.",
    refund: "Reembolso automático si el código no llega.",
    keywordsHeading: "Palabras clave que resuelve esta guía",
    keywordsSub: "Este tutorial responde a búsquedas como:",
    stepsHeading: (app) => `Pasos para crear ${app} sin número real`,
    ctaTitle: (app) => `Crea tu ${app} anónimo ahora`,
    ctaSub: "Alquila un número virtual desde 0,35 $ y recibe el SMS en segundos.",
    ctaLabel: "Ir a SMS Activations",
    tipsHeading: "Consejos para mantener tu cuenta anónima",
    faqHeading: "Preguntas frecuentes",
    back: "← Volver al blog",
    home: "Painel",
    blog: "Blog",
  },
  ru: {
    quickSummary: "Resumen rápido",
    cost: "Coste: menos de 0,99 $ por SMS.",
    time: "Tiempo total: 5 minutos.",
    privacy: "Privacidad: no expones tu número real.",
    refund: "Reembolso automático si el código no llega.",
    keywordsHeading: "Palabras clave que resuelve esta guía",
    keywordsSub: "Este tutorial responde a búsquedas como:",
    stepsHeading: (app) => `Pasos para crear ${app} sin número real`,
    ctaTitle: (app) => `Crea tu ${app} anónimo ahora`,
    ctaSub: "Alquila un número virtual desde 0,35 $ y recibe el SMS en segundos.",
    ctaLabel: "Ir a SMS Activations",
    tipsHeading: "Consejos para mantener tu cuenta anónima",
    faqHeading: "Preguntas frecuentes",
    back: "← Volver al blog",
    home: "Панель управления",
    blog: "Blog",
  },
  zh: {
    quickSummary: "Resumen rápido",
    cost: "Coste: menos de 0,99 $ por SMS.",
    time: "Tiempo total: 5 minutos.",
    privacy: "Privacidad: no expones tu número real.",
    refund: "Reembolso automático si el código no llega.",
    keywordsHeading: "Palabras clave que resuelve esta guía",
    keywordsSub: "Este tutorial responde a búsquedas como:",
    stepsHeading: (app) => `Pasos para crear ${app} sin número real`,
    ctaTitle: (app) => `Crea tu ${app} anónimo ahora`,
    ctaSub: "Alquila un número virtual desde 0,35 $ y recibe el SMS en segundos.",
    ctaLabel: "Ir a SMS Activations",
    tipsHeading: "Consejos para mantener tu cuenta anónima",
    faqHeading: "Preguntas frecuentes",
    back: "← Volver al blog",
    home: "仪表板",
    blog: "Blog",
  },
};

export const Route = createFileRoute("/$lang/blog/$slug")({
  beforeLoad: ({ params }) => {
    if (!isBlogLang(params.lang)) throw notFound();
  },
  // Dynamic import: `loader` and `head` are not code-split, so a static import of the
  // corpus here landed all 967 KB of posts in the chunk every page preloads.
  loader: async ({ params }) => {
    const lang = params.lang as BlogLang;
    const { getPost } = await import("@/lib/blog-posts");
    const post = getPost(lang, params.slug);
    if (!post) throw notFound();
    return post;
  },
  head: ({ params, loaderData }) => {
    if (!isBlogLang(params.lang)) return { meta: [{ title: "Post" }] };
    const lang = params.lang;
    // Only `loaderData` — `head` is synchronous, so it cannot await the corpus, and the
    // loader has already thrown notFound() for anything that does not resolve.
    const post = loaderData;
    if (!post) {
      return { meta: [{ title: lang === "es" ? "Post no encontrado" : "Post not found" }] };
    }
    const url = `${SITE_URL}/${lang}/blog/${post.slug}`;
    const altUrl =
      post.altSlug && post.altLang ? `${SITE_URL}/${post.altLang}/blog/${post.altSlug}` : undefined;
    return {
      meta: [
        { title: post.metaTitle },
        { name: "description", content: post.description },
        { name: "keywords", content: post.keywords },
        { property: "og:title", content: post.metaTitle },
        { property: "og:description", content: post.description },
        { property: "og:type", content: "article" },
        { property: "og:url", content: url },
        { property: "og:locale", content: lang === "es" ? "es_ES" : "en_US" },
        { property: "article:published_time", content: post.date },
        { property: "og:image", content: `${SITE_URL}/og/blog.png` },
        { property: "og:image:width", content: "1200" },
        { property: "og:image:height", content: "630" },
        { property: "og:image:alt", content: post.metaTitle },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: post.metaTitle },
        { name: "twitter:description", content: post.description },
        { name: "twitter:image", content: `${SITE_URL}/og/blog.png` },
      ],
      links: [
        { rel: "canonical", href: url },
        { rel: "alternate", hrefLang: lang, href: url },
        ...(altUrl && post.altLang
          ? [{ rel: "alternate", hrefLang: post.altLang, href: altUrl }]
          : []),
        { rel: "alternate", hrefLang: "x-default", href: altUrl ?? url },
      ],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "HowTo",
            name: post.title,
            description: post.description,
            inLanguage: lang,
            totalTime: "PT5M",
            estimatedCost: { "@type": "MonetaryAmount", currency: "USD", value: "0.99" },
            step: post.steps.map((s, i) => ({
              "@type": "HowToStep",
              position: i + 1,
              name: s.title,
              text: s.body,
            })),
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            headline: post.title,
            description: post.description,
            inLanguage: lang,
            datePublished: post.date,
            dateModified: post.updatedAt ?? post.date,
            author: { "@type": "Organization", name: "SmsEmpire" },
            publisher: {
              "@type": "Organization",
              name: "SmsEmpire",
              logo: { "@type": "ImageObject", url: `${SITE_URL}/favicon.ico` },
            },
            mainEntityOfPage: url,
            url,
            keywords: post.keywords,
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: post.faq.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: lang === "es" ? "Inicio" : "Home",
                item: `${SITE_URL}/`,
              },
              { "@type": "ListItem", position: 2, name: "Blog", item: `${SITE_URL}/${lang}/blog` },
              { "@type": "ListItem", position: 3, name: post.app ?? post.title, item: url },
            ],
          }),
        },
      ],
    };
  },
  component: LocalizedBlogPost,
  notFoundComponent: () => (
    <main className="mx-auto max-w-3xl px-6 py-24 text-center">
      <h1 className="text-3xl font-bold">Not found</h1>
      <Link to="/dashboard" className="mt-4 inline-block text-primary">
        Dashboard
      </Link>
    </main>
  ),
  errorComponent: ({ error }) => (
    <main className="mx-auto max-w-3xl px-6 py-24 text-center">
      <h1 className="text-2xl font-bold">Error loading post</h1>
      <p className="mt-2 text-sm text-muted-foreground">{error.message}</p>
    </main>
  ),
});

function LocalizedBlogPost() {
  const post = Route.useLoaderData() as BlogPost;
  const ui = UI[post.lang];
  const app = post.app ?? "";
  return (
    <article className="mx-auto max-w-3xl px-6 py-16">
      <nav className="mb-6 text-sm text-muted-foreground">
        <Link to="/dashboard" className="hover:text-primary">
          {ui.home}
        </Link>
        <span className="mx-2">/</span>
        <Link to="/$lang/blog" params={{ lang: post.lang }} className="hover:text-primary">
          {ui.blog}
        </Link>
        {app && (
          <>
            <span className="mx-2">/</span>
            <span className="text-foreground">{app}</span>
          </>
        )}
      </nav>

      <header>
        <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
          <time>{post.date}</time>
          <span className="inline-flex items-center gap-1">
            <Clock className="h-3.5 w-3.5" /> {post.readTime}
          </span>
          {app && (
            <span className="rounded-full bg-primary/10 px-2 py-0.5 font-medium text-primary">
              {app}
            </span>
          )}
          {post.altSlug && post.altLang && (
            <Link
              to="/$lang/blog/$slug"
              params={{ lang: post.altLang, slug: post.altSlug }}
              className="ml-auto rounded-full border border-border px-2 py-0.5 hover:border-primary hover:text-primary"
            >
              {post.altLang.toUpperCase()} version
            </Link>
          )}
        </div>
        <h1 className="mt-4 text-4xl font-bold tracking-tight sm:text-5xl">{post.title}</h1>
        <p className="mt-6 text-lg text-muted-foreground">{post.intro}</p>
      </header>

      <section className="mt-8 rounded-2xl border border-primary/30 bg-primary/5 p-5 text-sm">
        <p className="font-semibold text-foreground">{ui.quickSummary}</p>
        <ul className="mt-2 space-y-1 text-muted-foreground">
          <li className="flex items-start gap-2">
            <CheckCircle2 className="mt-0.5 h-4 w-4 text-primary" /> {ui.cost}
          </li>
          <li className="flex items-start gap-2">
            <Clock className="mt-0.5 h-4 w-4 text-primary" /> {ui.time}
          </li>
          <li className="flex items-start gap-2">
            <Lock className="mt-0.5 h-4 w-4 text-primary" /> {ui.privacy}
          </li>
          <li className="flex items-start gap-2">
            <ShieldCheck className="mt-0.5 h-4 w-4 text-primary" /> {ui.refund}
          </li>
        </ul>
      </section>

      <section className="mt-10">
        <h2 className="text-2xl font-bold">{ui.keywordsHeading}</h2>
        <p className="mt-2 text-sm text-muted-foreground">{ui.keywordsSub}</p>
        <ul className="mt-3 flex flex-wrap gap-2">
          {post.keywordsMatched.map((k) => (
            <li
              key={k}
              className="rounded-full border border-border bg-card px-3 py-1 text-xs text-muted-foreground"
            >
              «{k}»
            </li>
          ))}
        </ul>
      </section>

      <section className="mt-12">
        <h2 className="text-2xl font-bold">{ui.stepsHeading(app)}</h2>
        <div className="mt-6 space-y-6">
          {post.steps.map((s, index) => (
            <div key={s.title}>
              <div className="rounded-2xl border border-border bg-card p-6 shadow-elegant">
                <h3 className="text-lg font-semibold text-foreground">{s.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{s.body}</p>
              </div>
              {index === 1 && (
                <div className="mt-8 rounded-3xl border border-primary/30 bg-gradient-primary p-8 text-center shadow-glow">
                  <h2 className="text-2xl font-bold text-primary-foreground">{ui.ctaTitle(app)}</h2>
                  <p className="mt-2 text-sm text-primary-foreground/90">{ui.ctaSub}</p>
                  <Link
                    to={post.category === "accounts" ? "/accounts" : "/dashboard"}
                    className="mt-6 inline-flex items-center gap-2 rounded-full bg-background px-5 py-2 text-sm font-semibold text-foreground shadow-elegant hover:opacity-90"
                  >
                    {ui.ctaLabel} <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      <section className="mt-12">
        <h2 className="text-2xl font-bold">{ui.tipsHeading}</h2>
        <ul className="mt-4 space-y-2 text-sm text-muted-foreground">
          {post.tips.map((t) => (
            <li key={t} className="flex items-start gap-2">
              <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" /> {t}
            </li>
          ))}
        </ul>
      </section>

      <section className="mt-12">
        <h2 className="text-2xl font-bold">{ui.faqHeading}</h2>
        <div className="mt-6 space-y-3">
          {post.faq.map((f) => (
            <details
              key={f.q}
              className="group rounded-2xl border border-border bg-card p-5 open:shadow-glow"
            >
              <summary className="cursor-pointer list-none text-base font-semibold text-foreground">
                {f.q}
              </summary>
              <p className="mt-2 text-sm text-muted-foreground">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      <footer className="mt-12 border-t border-border pt-6 text-sm text-muted-foreground">
        <Link
          to="/$lang/blog"
          params={{ lang: post.lang }}
          className="text-primary hover:underline"
        >
          {ui.back}
        </Link>
        {SUPPORTED_LANGS.length > 1 && (
          <span className="ml-4">
            {SUPPORTED_LANGS.map((l) => (
              <Link
                key={l}
                to="/$lang/blog"
                params={{ lang: l }}
                className={`ml-2 rounded border px-2 py-0.5 text-xs ${
                  l === post.lang
                    ? "border-primary text-primary"
                    : "border-border hover:border-primary hover:text-primary"
                }`}
              >
                {l.toUpperCase()}
              </Link>
            ))}
          </span>
        )}
      </footer>
    </article>
  );
}
